import request from '@/utils/request'
import { v2Host } from '@/config/host'
import { AxiosPromise } from 'axios'
import {
  AdvertiserListRequestParams,
  CheckAdvertiserRequestParams,
  SaveAdvertiserInfoRequestParams,
  AdvertiserAccountListRequestParams,
} from '@/types/advertiser/index.type'
import type { advertiserDataConfig } from '@/api/advertiser/manage.type'
import { PermissionKey } from '@pt/enums/user'
import { cookRequestParams } from '@/common/request'

// http://playable.dev.com:8067/?doc=advertiser/api%2fadvertiser%2fadvertiser.go#-advertiser-manage-create

export function fetchAdvertiserList(
  params: AdvertiserListRequestParams,
): AxiosPromise<advertiserDataConfig> {
  return request({
    url: v2Host + '/advertiser/manage/list',
    method: 'get',
    params: params,
  })
}

export function createAdvertiser(
  data: SaveAdvertiserInfoRequestParams,
): AxiosPromise {
  return request({
    url: v2Host + '/advertiser/manage/create',
    method: 'post',
    data,
  })
}

export function updateAdvertiser(
  id: number,
  data: SaveAdvertiserInfoRequestParams,
): AxiosPromise {
  if (!data.password) delete data.password // password 为空时不需要传
  return request({
    url: v2Host + '/advertiser/manage/modify',
    method: 'post',
    params: {
      id,
    },
    data,
  })
}

export function loginAdvertiser(data: { id: number }): AxiosPromise {
  return request({
    url: v2Host + '/advertiser/manage/login-adv',
    method: 'post',
    data,
  })
}

export function fetchAdvertiserInfo(params: {
  id?: number
  name?: string
  email?: string
  is_acs?: number
}): AxiosPromise {
  return request({
    url: v2Host + '/advertiser/manage/info',
    method: 'get',
    params: params,
  })
}

export function checkAdvertiserIsExist(
  params: CheckAdvertiserRequestParams,
): AxiosPromise {
  return request({
    url: v2Host + '/advertiser/manage/check',
    method: 'get',
    params: params,
  })
}

export function getAdvertiserSetting(args: {
  params: {
    id: number
  }
}): AxiosPromise {
  return request({
    url: v2Host + '/advertiser/manage/setting',
    method: 'get',
    params: args.params,
  })
}

export function setAdvertiserSetting(args: {
  params: {
    id: number
  }
  data: {
    creative_final_review: number
    pt_allow_down_creative_file: number
    pt_show_dmaker_template: number
    pt_show_gmaker_template: number
    inner_test_permissions: Array<PermissionKey.Insights>
  }
}): AxiosPromise {
  return request({
    url: v2Host + '/advertiser/manage/setting',
    method: 'post',
    params: args.params,
    data: args.data,
  })
}

export function getAdvertiserOptions(
  params: { types?: string } = {},
): AxiosPromise {
  return request({
    url: v2Host + '/advertiser/manage/options',
    method: 'get',
    params,
  })
}

export function advertiserAccountList(
  params: AdvertiserAccountListRequestParams,
): AxiosPromise<advertiserDataConfig> {
  return request({
    url: v2Host + '/advertiser/ad-account/list',
    method: 'get',
    params: cookRequestParams(params),
  })
}

export function adAccountUnbind(data: { id: number }): AxiosPromise {
  return request({
    url: v2Host + '/advertiser/ad-account/unbind',
    method: 'post',
    data,
  })
}

export function adAccountDelete(data: { id: number }): AxiosPromise {
  return request({
    url: v2Host + '/advertiser/ad-account/delete',
    method: 'post',
    data,
  })
}

export function advWarningAdvertiser(data: {
  adv_names: string[]
  is_use_review: number
  is_use_rereview: number
}): AxiosPromise {
  return request({
    url: v2Host + '/creative/warning/save-adv-warning',
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
  })
}

export function delAdvWarning(adv_names: string[]): AxiosPromise {
  return request({
    url: v2Host + '/creative/warning/del-adv-warning',
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
    },
    data: {
      adv_names,
    },
  })
}

export function delWarningAdvertiser(adv_names: string[]): AxiosPromise {
  return request({
    url: v2Host + '/advertiser/manage/del-adv-warning',
    method: 'post',
    data: {
      adv_names,
    },
  })
}

export function warningListAdvertiser(params: {
  name: string
  page: number
  page_size: number
}): AxiosPromise {
  return request({
    url: v2Host + '/advertiser/manage/warning-list',
    method: 'get',
    params: cookRequestParams(params),
  })
}

export function updateUserReview(data: {
  adv_names: string[]
  is_use_review: number
  is_use_rereview: number
  upd_review: boolean
}): AxiosPromise {
  return request({
    url: v2Host + '/creative/warning/upd-use-review',
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
  })
}

export function testAccountList(params: {
  search_field?: string
  keyword?: string
  page: number
  page_size: number
}): AxiosPromise {
  return request({
    url: v2Host + '/advertiser/manage/test-account-list',
    method: 'get',
    params: cookRequestParams(params),
  })
}

export function addTestAccountList(adv_ids: number[]): AxiosPromise {
  return request({
    url: v2Host + '/advertiser/manage/add-test-account',
    method: 'post',
    data: {
      adv_ids,
    },
  })
}

export function delTestAccountList(ids: number[]): AxiosPromise {
  return request({
    url: v2Host + '/advertiser/manage/del-test-account',
    method: 'post',
    data: {
      ids,
    },
  })
}

/**
 * 添加协助人
 */
export function addAdvertiserAssistant(data: {
  id: number
  assistant_name: string
}): AxiosPromise {
  return request({
    url: `${v2Host}/advertiser/ad-account/add-assistant`,
    method: 'post',
    data,
  })
}

/**
 * 协助账号列表
 */
export function getAdvertiserAssistantsList(params: {
  id: number
  page_size: number
  page: number
}): AxiosPromise {
  return request({
    url: `${v2Host}/advertiser/ad-account/list-assistants`,
    method: 'get',
    params,
  })
}

/**
 * 删除协助人
 */
export function deleteAdvertiserAssistant(data: { id: number }): AxiosPromise {
  return request({
    url: `${v2Host}/advertiser/ad-account/del-assistant`,
    method: 'post',
    data,
  })
}

/**
 * 获取合作方信息
 */
export function getAdvertiserPartnerInfo(params: {
  adv_id: number
}): AxiosPromise {
  return request({
    url: `${v2Host}/advertiser/manage/partner-info`,
    method: 'get',
    params,
  })
}
